import { ErrorMessage, Spinner } from "@/components";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useInternshipByIdQuery } from "@/hooks/api/internship.queries";
import {
  ArrowLeft,
  BookOpen,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  DollarSign,
  FileText,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  User,
  Users,
  XCircle,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";

const InternshipDetailPlaceholder = () => {
  return (
    <div className="flex justify-center py-16">
      <Spinner />
    </div>
  );
};

const InternshipsView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: internship,
    isLoading,
    isError,
  } = useInternshipByIdQuery(id || "");

  if (isLoading) return <InternshipDetailPlaceholder />;
  if (isError || !internship) {
    return (
      <ErrorMessage
        message="Failed to load internship details."
        onRetry={() => navigate("/activities/internships")}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/activities/internships")}
            className="mt-1 h-8 w-8 rounded-full border border-gray-200"
          >
            <ArrowLeft size={16} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {internship.position}
            </h1>
            <p className="text-gray-500">
              {/* placeholder vendor name mapping or use ID */}
              Vendor ID: {internship.company}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Badge
            variant={internship.status === "active" ? "default" : "secondary"}
            className="bg-black px-3 py-1 text-xs uppercase text-white hover:bg-black/90"
          >
            {internship.status}
          </Badge>
          <Badge
            variant="outline"
            className="bg-black px-3 py-1 text-xs text-white hover:bg-black/90"
          >
            {internship.mode}
          </Badge>
        </div>
      </div>

      {/* Top Metric Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-blue-600">
              <Clock size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Duration</p>
              <p className="font-semibold text-gray-900">
                {internship.duration}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-50 text-green-600">
              <DollarSign size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Stipend</p>
              <p className="font-semibold text-gray-900">
                ${internship.stipend.toLocaleString()}/month
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-50 text-purple-600">
              <MapPin size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Location</p>
              <p className="font-semibold text-gray-900">
                {internship.mode === "Remote" ? "Remote" : "Mountain View, CA"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card className="rounded-2xl border border-gray-100 shadow-sm">
          <CardContent className="flex items-center gap-4 p-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-orange-50 text-orange-600">
              <Calendar size={20} />
            </div>
            <div>
              <p className="text-xs text-gray-500">Start Date</p>
              <p className="font-semibold text-gray-900">
                {new Date(internship.startDate).toLocaleDateString("en-GB")}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Left Column */}
        <div className="space-y-6 lg:col-span-2">
          {/* Internship Details */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <FileText size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Internship Details
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              {internship.description && (
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900">
                    Description
                  </h4>
                  <p className="text-sm leading-relaxed text-gray-500">
                    {internship.description}
                  </p>
                </div>
              )}

              {/* Placeholder Responsibilities */}
              <div>
                <h4 className="mb-2 font-semibold text-gray-900">
                  Responsibilities
                </h4>
                <ul className="list-inside list-disc space-y-2 text-sm text-gray-500">
                  <li>Develop and maintain software applications</li>
                  <li>Collaborate with cross-functional teams</li>
                  <li>Participate in code reviews and testing</li>
                  <li>Learn industry best practices and tools</li>
                </ul>
              </div>

              {internship.requirements && (
                <div>
                  <h4 className="mb-2 font-semibold text-gray-900">
                    Requirements
                  </h4>
                  <ul className="list-inside list-disc space-y-2 text-sm text-gray-500">
                    {internship.requirements.split("\n").map((req, i) => (
                      <li key={i}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              <hr className="border-gray-100" />

              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div>
                  <h4 className="mb-4 font-semibold text-gray-900">
                    Duration Details
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Start Date:</span>
                      <span className="font-medium text-gray-900">
                        {new Date(internship.startDate).toLocaleDateString(
                          "en-GB"
                        )}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">End Date:</span>
                      <span className="font-medium text-gray-900">
                        {internship.endDate
                          ? new Date(internship.endDate).toLocaleDateString(
                              "en-GB"
                            )
                          : "TBD"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Weekly Hours:</span>
                      <span className="font-medium text-gray-900">
                        40 hours
                      </span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="mb-4 font-semibold text-gray-900">
                    Compensation
                  </h4>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Stipend:</span>
                      <span className="font-bold text-gray-900">
                        ${internship.stipend.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Type:</span>
                      <span className="font-medium text-gray-900">
                        per month
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Skills & Learning */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <BookOpen size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Skills & Learning
              </h3>
            </div>
            <CardContent className="grid grid-cols-1 gap-8 p-6 md:grid-cols-2">
              <div>
                <h4 className="mb-4 font-semibold text-gray-900">
                  Skills Required
                </h4>
                <div className="flex flex-wrap gap-2">
                  {["Java", "Python", "Git", "Problem Solving"].map((s) => (
                    <Badge
                      key={s}
                      variant="secondary"
                      className="bg-gray-100 text-gray-600 hover:bg-gray-200"
                    >
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <h4 className="mb-4 font-semibold text-gray-900">
                  Skills to be Gained
                </h4>
                <div className="flex flex-wrap gap-2">
                  {[
                    "Software Development",
                    "Code Review",
                    "Team Collaboration",
                    "Agile Methodology",
                  ].map((s) => (
                    <Badge
                      key={s}
                      variant="secondary"
                      className="border-none bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                      {s}
                    </Badge>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Related Training Programs */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Briefcase size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Related Training Programs
              </h3>
            </div>
            <CardContent className="space-y-4 p-6">
              <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <CheckCircle2 size={16} />
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900">
                      Full Stack Development
                    </h5>
                    <p className="text-xs text-gray-500">Tech Skills</p>
                  </div>
                </div>
                <Badge
                  variant="secondary"
                  className="rounded-lg bg-gray-200/50 text-gray-700"
                >
                  Completed
                </Badge>
              </div>

              <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <CheckCircle2 size={16} />
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-gray-900">
                      Communication Skills
                    </h5>
                    <p className="text-xs text-gray-500">Soft Skills</p>
                  </div>
                </div>
                <Badge
                  variant="secondary"
                  className="rounded-lg bg-gray-200/50 text-gray-700"
                >
                  Completed
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Enrolled Students */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
              <div className="flex items-center gap-2">
                <Users size={18} className="text-gray-700" />
                <h3 className="text-lg font-bold text-gray-900">
                  Enrolled Students ({internship.students.length})
                </h3>
              </div>
            </div>
            <CardContent className="space-y-6 p-6">
              {internship.students.map((studentId, idx) => (
                <div
                  key={studentId}
                  className="space-y-4 rounded-2xl border border-gray-100 p-5"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 font-bold text-blue-600">
                        <User size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">
                          Student {studentId}
                        </h4>
                        <div className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                          <span>CSE202100{idx + 1}</span>
                          <Badge
                            variant="outline"
                            className="h-5 px-1.5 text-[10px]"
                          >
                            CSE
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Badge className="bg-black px-3 py-1 text-[10px] uppercase text-white">
                      Active
                    </Badge>
                  </div>

                  <div className="space-y-2 text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <Mail size={14} className="text-gray-400" />
                      <span>student{studentId}@college.edu</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone size={14} className="text-gray-400" />
                      <span>+1-234-567-900{idx + 1}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-xl border border-blue-100/50 bg-blue-50/50 p-4">
                      <p className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                        <span className="text-blue-500">📈</span> Evaluation
                        Score
                      </p>
                      <p className="text-2xl font-bold text-blue-700">85</p>
                      <p className="mt-1 text-xs text-gray-400">out of 100</p>
                    </div>
                    <div className="rounded-xl border border-green-100/50 bg-green-50/50 p-4">
                      <p className="mb-1 flex items-center gap-1 text-xs text-gray-500">
                        <CheckCircle2 size={12} className="text-green-500" />{" "}
                        Attendance
                      </p>
                      <p className="text-2xl font-bold text-green-700">95%</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm text-gray-600">
                    <XCircle size={16} className="text-gray-400" />
                    <div>
                      <p className="font-semibold text-gray-900">
                        Certificate Not Issued
                      </p>
                      <p className="text-xs text-gray-500">
                        Pending completion
                      </p>
                    </div>
                  </div>

                  <div className="rounded-xl border border-yellow-200 bg-yellow-50/50 p-4">
                    <p className="mb-1 text-xs font-semibold text-yellow-800">
                      Remarks
                    </p>
                    <p className="text-sm text-yellow-700">
                      Excellent performance and quick learner
                    </p>
                  </div>

                  <Button variant="outline" className="w-full rounded-xl">
                    View Student Profile
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Company Information */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Building2 size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Company Information
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              <div>
                <p className="mb-1 text-sm text-gray-500">Company Name</p>
                <p className="font-semibold text-gray-900">
                  Vendor ID: {internship.company}
                </p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-500">Location</p>
                <p className="text-sm leading-relaxed text-gray-900">
                  1600 Amphitheatre Parkway, Mountain View, CA
                </p>
              </div>
              <hr className="border-gray-100" />
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 text-sm text-blue-600">
                  <Mail size={16} />
                  <a href="mailto:hr@company.com" className="hover:underline">
                    hr@company.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Phone size={16} />
                  <span>+1-650-253-0000</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* College Mentor */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <GraduationCap size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                College Mentor
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              <div>
                <p className="mb-1 text-sm text-gray-500">Name</p>
                <p className="font-semibold text-gray-900">
                  Prof. Sarah Johnson
                </p>
              </div>
              <div>
                <p className="mb-2 text-sm text-gray-500">Department</p>
                <Badge
                  variant="outline"
                  className="rounded-md border-gray-200 text-xs text-gray-600 shadow-sm"
                >
                  CSE
                </Badge>
              </div>
              <hr className="border-gray-100" />
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Mail size={16} />
                  <a
                    href="mailto:sarah.j@college.edu"
                    className="hover:underline"
                  >
                    sarah.j@college.edu
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Phone size={16} />
                  <span>+1-234-567-8901</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Company Supervisor */}
          <Card className="rounded-2xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-2 border-b border-gray-100 px-6 py-4">
              <Users size={18} className="text-gray-700" />
              <h3 className="text-lg font-bold text-gray-900">
                Company Supervisor
              </h3>
            </div>
            <CardContent className="space-y-6 p-6">
              <div>
                <p className="mb-1 text-sm text-gray-500">Name</p>
                <p className="font-semibold text-gray-900">John Smith</p>
              </div>
              <div>
                <p className="mb-1 text-sm text-gray-500">Designation</p>
                <p className="text-sm text-gray-700">
                  Senior Software Engineer
                </p>
              </div>
              <hr className="border-gray-100" />
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-blue-600">
                  <Mail size={16} />
                  <a
                    href="mailto:john.smith@company.com"
                    className="hover:underline"
                  >
                    john.smith@company.com
                  </a>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Phone size={16} />
                  <span>+1-987-654-3210</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Certificate Status */}
          <Card className="rounded-2xl border border-gray-100 bg-gray-50/50 shadow-sm">
            <CardContent className="p-6">
              <h3 className="mb-4 text-base font-bold text-gray-900">
                Certificate Status
              </h3>
              <div className="flex items-center gap-3 rounded-xl border border-gray-100 bg-white p-4 shadow-sm">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400">
                  <XCircle size={18} />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">Not Issued</p>
                  <p className="text-xs text-gray-500">Pending completion</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default InternshipsView;
